// Eklenti Sağlığı çekmecesi (tasarım B, "Extension Toolkit.dc.html") — Canlı Durum'un üstünde sağdan
// açılır, ızgara görünür kalır (admin temsilciyle telefondayken). Yalnız admin: komut geçmişi ve
// komutlar zaten admin-only (src/api/commands.js), çekmece de öyle.
//
// Veri: GET /api/agents/:id/ext-health (son durum raporu + günlük + komutlar + bugünün olayları) +
// Canlı Durum'un kendi LiveAgent satırı (heartbeat, telemetri, sürüm). Karar mantığı saf
// `ext-health.ts`'te; burası yalnız çizim + komut gönderme.
//
// Tasarımda olup BURADA olmayanlar (yeni eklenti sürümü ister): "Bir sonraki açılışta uygula"
// (eklenti 15 dk'dan eski komutu reddeder — CMD_MAX_AGE_MS), hata AKIŞI (şimdilik son tail_logs
// çekiminin gruplanması), debug modu, update_extension / factory_reset, kurulum türü / Chrome sürümü.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAgentCommands, getExtHealth, issueAgentCommand, cancelAgentCommand } from '../api';
import { brandLabel, RULE_TITLES, STATE_META, tzOfBrands } from '../constants';
import {
  atLeast, minVersionFor, supportsKind, TOOLKIT_MIN_VERSION,
  confirmTier, fmtDur, fmtRejectedRow, groupErrors, healthRows, rejectedRowOf, isLiveStatus, kindLabel, L, LADDER, nextFix,
  parseLogLine, statusBadge, summarizeResult, type HealthRow, type Lang, type Level, type LogLine,
} from '../ext-health';
import { fmtClock } from '../format';
import { useNow } from '../hooks';
import type { AgentCommand, ExtErrorGroup, ExtHealth, ExtLogLine, LiveAgent } from '../types';
import { dotStyle, Icon, inputStyle } from '../ui';
import ExtReportModal, { installLabel, REPORT_STATUS_META } from './ExtReportModal';

type TabKey = 'durum' | 'hatalar' | 'gunluk' | 'zaman' | 'ayarlar' | 'islemler';

const C = { ok: '#37c46b', warn: '#e0a23c', bad: '#ef5d5d', info: '#3d8bff', none: '#6b7280' };
const levelColor = (l: Level | 'info') => C[l];
const INNER = 'var(--inset)';
const ROWLINE = 'var(--rowline)';
const MONO = 'ui-monospace,SFMono-Regular,Menlo,monospace';

const pill = (c: string): React.CSSProperties => ({
  fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', color: c,
  background: c + '1a', border: '1px solid ' + c + '40', whiteSpace: 'nowrap', flex: 'none',
});
const chip: React.CSSProperties = {
  fontSize: '9.5px', fontWeight: 700, color: 'var(--dim)', background: 'var(--surface2)',
  border: '1px solid var(--border)', borderRadius: '5px', padding: '2px 6px', whiteSpace: 'nowrap',
};
const seg = (on: boolean): React.CSSProperties => ({
  padding: '6px 11px', borderRadius: '7px', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer',
  border: '1px solid ' + (on ? 'var(--accent-border)' : 'transparent'),
  background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--text)' : 'var(--dim)',
});
const btn = (kind: 'primary' | 'ghost' | 'busy', disabled = false): React.CSSProperties => {
  const base: React.CSSProperties = { padding: '6px 12px', borderRadius: '8px', fontSize: '11.5px', fontWeight: 600, whiteSpace: 'nowrap', flex: 'none' };
  if (disabled) return { ...base, background: 'transparent', border: '1px solid var(--border)', color: 'var(--placeholder)', cursor: 'not-allowed' };
  if (kind === 'primary') return { ...base, background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', cursor: 'pointer' };
  if (kind === 'busy') return { ...base, background: 'transparent', border: '1px solid var(--border2)', color: C.warn, cursor: 'pointer' };
  return { ...base, background: 'transparent', border: '1px solid var(--accent-border)', color: 'var(--accent-text)', cursor: 'pointer' };
};
const tierStyle = (t: 1 | 2 | 3): React.CSSProperties => {
  const c = t === 3 ? C.bad : t === 2 ? C.warn : '#9aa4b2';
  return { fontSize: '9.5px', fontWeight: 700, color: c, background: c + '1a', border: '1px solid ' + c + '40', borderRadius: '5px', padding: '1px 5px' };
};

/** Ladder açıklamaları — sabit TR metin (sözlük çevirir). */
const LADDER_TEXT: Record<string, [string, string]> = {
  ping: ['Bağlantı testi', 'Panel ulaşılabilir mi? Hiçbir şeyi değiştirmez.'],
  diagnostics: ['Durum raporu al', 'Durum listesini yeniler. Salt okunur.'],
  flush_wal: ['Bekleyen sonuçları gönder', '"Kuyrukta" takılan sonuçları iter.'],
  clear_cache: ['Panel önbelleğini temizle', 'Mola durumuna ve bekleyen sonuçlara dokunmaz.'],
  recover_panel: ['Yumuşak onarım', 'Paneli yerinde yeniden başlatır, sayfa yenilenmez. Temsilci sonuç giriyorsa reddedilir.'],
  reload: ['Sayfayı yenile', 'Sayfayı tamamen yeniler. Temsilci sonuç giriyorsa reddedilir.'],
  reset_agent: ['Paneli tamamen sıfırla', 'Temsilcinin adı yeniden sorulur; bekleyen sonuçlar korunur.'],
  update_extension: ['Eklentiyi güncelle', 'Mağazada güncelleme arar; varsa temsilci boştayken eklentiyi ve sayfayı yeniden yükler.'],
  factory_reset: ['Fabrika ayarlarına döndür', 'Bekleyen sonuçlar dışındaki tüm ayar ve önbellekleri siler; ad ve şifre yeniden sorulur.'],
};

const ERR_TEXT: Record<string, [string, string]> = {
  command_in_flight: ['Bu temsilcinin bekleyen bir komutu var.', 'This agent already has a pending command.'],
  agent_has_no_brand: ['Temsilcinin markası yok — komut teslim edilemez.', 'The agent has no brand — a command cannot be delivered.'],
  not_found: ['Temsilci bulunamadı ya da kapsam dışında.', 'Agent not found or out of scope.'],
  deferred_unsupported: ['Ertelenmiş komut için eklenti 2.3.0 veya üstü gerekir.', 'A deferred command needs extension 2.3.0 or later.'],
  network_error: ['Sunucuya ulaşılamadı.', 'Could not reach the server.'],
};
const errText = (e: unknown, lang: Lang): string => {
  const code = e instanceof Error ? e.message : '';
  const m = ERR_TEXT[code];
  return m ? L(lang, m[0], m[1]) : L(lang, 'Komut gönderilemedi', 'Command could not be sent') + (code ? ' — ' + code : '');
};

interface QfStep { kind: string; state: 'run' | 'done' | 'fail'; text: string }
interface QuickFix { steps: QfStep[]; phase: 'running' | 'suggest' | 'done' | 'blocked'; suggestion: HealthRow | null; note: string }
interface Confirm { kind: string; tier: 1 | 2 | 3; go: () => void }

const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

/** Sağlık satırları bu yaştan eski durum raporuna DAYANMAZ (canlıda son rapor aylar önceki olabilir —
 *  2026-10-09 ölçümü: filonun en yeni diagnostics'i 2026-08-06). Ayarlar sekmesi tarihiyle yine gösterir. */
const DIAG_FRESH_MS = 2 * 3600_000;

export default function ExtHealthDrawer({
  agent,
  newest,
  outdated,
  lang,
  onClose,
  onOpenAllCommands,
}: {
  agent: LiveAgent;
  newest: string;
  outdated: boolean;
  lang: Lang;
  onClose: () => void;
  onOpenAllCommands: () => void;
}) {
  const [health, setHealth] = useState<ExtHealth | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [tab, setTab] = useState<TabKey>('durum');
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [typed, setTyped] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [sending, setSending] = useState(false);
  const [qf, setQf] = useState<QuickFix | null>(null);
  const [dismissed, setDismissed] = useState<string>('');
  const [errLevel, setErrLevel] = useState<'all' | 'error' | 'warn'>('all');
  const [errRange, setErrRange] = useState<'today' | '7d'>('today');
  const [logCat, setLogCat] = useState('all');
  const [queueNext, setQueueNext] = useState(false);
  const [paused, setPaused] = useState<ExtLogLine[] | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const alive = useRef(true);
  const now = useNow(5000);
  const tz = tzOfBrands(agent.brands);

  const load = useCallback(async () => {
    try {
      const h = await getExtHealth(agent.id);
      if (alive.current) { setHealth(h); setLoadErr(''); }
      return h;
    } catch (e) {
      if (alive.current) setLoadErr(e instanceof Error ? e.message : 'load_failed');
      return null;
    }
  }, [agent.id]);

  const live = health?.commands.find((c) => isLiveStatus(c.status)) || null;
  const running = !!live || qf?.phase === 'running';

  useEffect(() => {
    alive.current = true;
    void load();
    return () => { alive.current = false; };
  }, [load]);
  // Bekleyen komut / Hızlı düzelt varken 4 sn, yoksa 15 sn (teslimat panelin 30–60 sn'lik yoklamasına biner).
  useEffect(() => {
    const t = window.setInterval(() => void load(), running ? 4000 : 15000);
    return () => window.clearInterval(t);
  }, [load, running]);
  useEffect(() => {
    // Esc closes the TOP layer only: the confirm, then the report modal, and never the drawer under a modal opened
    // on top of it (the page's "Tüm komutlar" — any shared <Modal> carries data-modal).
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirm) setConfirm(null);
      else if (reportId) setReportId(null);
      else if (!document.querySelector('[data-modal]')) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirm, reportId, onClose]);

  const dark = agent.panelDark;
  const closed = agent.state === 'offline' && !dark;
  const diagAtMs = health?.diagnostics?.at ? new Date(health.diagnostics.at).getTime() : null;
  const diagFresh = diagAtMs != null && now - diagAtMs < DIAG_FRESH_MS;
  const diag = diagFresh ? health?.diagnostics?.result || null : null;
  const diagAny = health?.diagnostics?.result || null;
  const rows = useMemo(
    () => healthRows(agent, diag, health?.clock?.offsetMs ?? null, outdated, newest, now, lang),
    [agent, diag, health?.clock?.offsetMs, outdated, newest, now, lang],
  );
  const redCount = rows.filter((r) => r.level === 'bad').length;
  const logLines = useMemo(
    () => (health?.logs?.lines || []).map((l) => parseLogLine(l, tz)).filter((l): l is LogLine => !!l),
    [health?.logs?.lines, tz],
  );
  const groups = useMemo(() => groupErrors(logLines), [logLines]);
  // Faz 2 (2.3.0+): eklentinin KENDİ gönderdiği satırlar ve gruplar. Eski sürümde yok → yukarıdaki tail_logs yolu.
  const toolkit = atLeast(agent.extVersion, TOOLKIT_MIN_VERSION);
  const pushed = health?.lines || [];
  const dbGroups = (health?.errors || []).filter((g) => (errRange === 'today' ? g.countToday : g.count7d) > 0);
  const useDb = toolkit || (health?.errors?.length ?? 0) > 0;
  // Debug modu: en son biten debug_logging komutunun `until`'i hâlâ gelecekteyse açık.
  // The newer of the last debug_logging answer and the last diagnostics' `debugUntil` (the per-tab truth: a new tab or a
  // browser restart has no window although the command said so). Both are the AGENT's clock — shifted by the measured
  // offset (server − agent) before comparing with this browser's clock.
  const debugUntil = (() => {
    const c = health?.commands.find((x) => x.kind === 'debug_logging' && x.status === 'done');
    const cAt = c?.done_at ? Date.parse(c.done_at) : NaN;
    const dAt = health?.diagnostics?.at ? Date.parse(health.diagnostics.at) : NaN;
    const fromDiag = Number.isFinite(dAt) && (!Number.isFinite(cAt) || dAt > cAt);
    const raw = fromDiag ? (health?.diagnostics?.result as Record<string, unknown> | null)?.debugUntil : c?.result?.until;
    const u = typeof raw === 'string' ? Date.parse(raw) : NaN;
    const off = health?.clock?.offsetMs ?? 0;
    return Number.isFinite(u) && u + off > now ? u + off : null;
  })();

  // ── komut gönderme ─────────────────────────────────────────────────────────
  /** Bir komutu gönderir ve bitene kadar bekler (Hızlı düzelt). Bitmemişse null. */
  const waitFor = async (cmdId: string): Promise<AgentCommand | null> => {
    const deadline = Date.now() + 16 * 60_000;
    while (alive.current && Date.now() < deadline) {
      await sleep(3000);
      try {
        const r = await getAgentCommands(agent.id);
        const c = r.commands.find((x) => x.id === cmdId);
        if (c && c.done_at) return c;
      } catch { /* bir sonraki turda tekrar */ }
    }
    return null;
  };

  const issue = async (kind: string, args: Record<string, unknown> = {}, deferred = false): Promise<AgentCommand | null> => {
    setActionErr('');
    setSending(true);
    try {
      const r = await issueAgentCommand(agent.id, kind, args, { deferred });
      setDismissed('');
      void load();
      return r.command;
    } catch (e) {
      setActionErr(errText(e, lang));
      return null;
    } finally {
      setSending(false);
    }
  };

  /** Onay kuralı: tier 1 doğrudan, 2 pencere, 3 adını yaz. `deferred` = "bir sonraki açılışta uygula" (T2). */
  const request = (kind: string, then?: () => void, args: Record<string, unknown> = {}, deferred = false) => {
    const tier = confirmTier(kind);
    const go = () => { setConfirm(null); if (then) then(); else void issue(kind, args, deferred); };
    if (tier === 1) go();
    else { setTyped(''); setConfirm({ kind, tier, go }); }
  };

  const cancel = async (c: AgentCommand) => {
    try {
      const r = await cancelAgentCommand(agent.id, c.id);
      if (!r.cancelled) setActionErr(L(lang, 'İptal edilemedi — komut panele ulaşmış.', 'Could not cancel — the panel already has it.'));
    } catch (e) {
      setActionErr(errText(e, lang));
    }
    void load();
  };

  // ── Hızlı düzelt (öner modu: adım adım, her düzeltmeden sonra durum raporu yenilenir) ──
  const step = async (kind: string, acc: QfStep[]): Promise<{ ok: boolean; steps: QfStep[] }> => {
    let steps = [...acc, { kind, state: 'run' as const, text: L(lang, 'Çalışıyor…', 'Running…') }];
    setQf((q) => (q ? { ...q, steps, phase: 'running' } : q));
    const cmd = await issue(kind);
    const done = cmd ? await waitFor(cmd.id) : null;
    const ok = !!done && done.status === 'done';
    const text = done ? summarizeResult(kind, done.status, done.result, lang, tz) : L(lang, 'Gönderilemedi ya da yanıt gelmedi', 'Not sent or no answer');
    steps = [...acc, { kind, state: ok ? 'done' : 'fail', text }];
    setQf((q) => (q ? { ...q, steps } : q));
    return { ok, steps };
  };

  const recheck = async (acc: QfStep[]) => {
    const d = await step('diagnostics', acc);
    if (!d.ok) { setQf((q) => (q ? { ...q, phase: 'blocked', note: L(lang, 'Durum raporu alınamadı', 'Diagnostics did not come back') } : q)); return; }
    const h = await load();
    const fresh = healthRows(agent, h?.diagnostics?.result || null, h?.clock?.offsetMs ?? null, outdated, newest, Date.now(), lang);
    const next = nextFix(fresh);
    setQf((q) => (q ? {
      ...q, steps: d.steps, phase: next ? 'suggest' : 'done', suggestion: next,
      note: next ? '' : L(lang, 'Düzeltilecek kırmızı/amber satır kalmadı', 'No fixable red or amber rows left'),
    } : q));
  };

  const startQuickFix = async () => {
    setTab('durum');
    setQf({ steps: [], phase: 'running', suggestion: null, note: '' });
    const p = await step('ping', []);
    if (!p.ok) { setQf((q) => (q ? { ...q, phase: 'blocked', note: L(lang, 'Panel yanıt vermedi — temsilciyi arayın', 'The panel did not answer — call the agent') } : q)); return; }
    await recheck(p.steps);
  };

  const runSuggestion = () => {
    const kind = qf?.suggestion?.fix;
    if (!kind || !qf) return;
    const acc = qf.steps;
    request(kind, () => {
      void (async () => {
        const s = await step(kind, acc);
        if (!s.ok) { setQf((q) => (q ? { ...q, phase: 'blocked', note: L(lang, 'Adım başarısız — geçmişe bakın', 'Step failed — see the history') } : q)); return; }
        await recheck(s.steps);
      })();
    });
  };

  // ── buton durumu ───────────────────────────────────────────────────────────
  const blockedWhy = dark ? L(lang, 'Panel karanlık', 'Panel dark') : closed ? L(lang, 'Panel kapalı', 'Panel closed') : '';
  // Bir komut, panel ALMADAN (deferred ise: ÇALIŞTIRMADAN) iptal edilebilir — sunucu accepted_at'e bakar.
  const cancellable = (c: AgentCommand) => !c.delivered_at || (c.deferred === true && !c.accepted_at);
  const actBtn = (kind: string, label: string, primary = false, args: Record<string, unknown> = {}) => {
    if (!supportsKind(agent.extVersion, kind)) return { label: L(lang, 'Sürüm ≥ ' + minVersionFor(kind) + ' gerekli', 'Needs v ≥ ' + minVersionFor(kind)), disabled: true, style: btn('ghost', true), onClick: undefined };
    if (live) return live.kind === kind && cancellable(live)
      ? { label: L(lang, 'İptal', 'Cancel'), disabled: false, style: btn('busy'), onClick: () => void cancel(live) }
      : { label: L(lang, 'Meşgul', 'Busy'), disabled: true, style: btn('ghost', true), onClick: undefined };
    if (sending || qf?.phase === 'running') return { label: L(lang, 'Meşgul', 'Busy'), disabled: true, style: btn('ghost', true), onClick: undefined };
    // Kapalı/karanlık panel: 2.3.0+ ve "açılışta uygula" işaretliyse sıraya alınır (T2), değilse gönderilmez.
    if (blockedWhy) return toolkit && queueNext
      ? { label: L(lang, 'Sıraya al', 'Queue'), disabled: false, style: btn('ghost'), onClick: () => request(kind, undefined, args, true) }
      : { label: blockedWhy, disabled: true, style: btn('ghost', true), onClick: undefined };
    return { label, disabled: false, style: btn(primary ? 'primary' : 'ghost'), onClick: () => request(kind, undefined, args) };
  };

  // ── şerit: canlı ya da az önce biten komut ──────────────────────────────────
  const latest = health?.commands[0] || null;
  const stripCmd = live || (latest && latest.done_at && latest.id !== dismissed && now - new Date(latest.done_at).getTime() < 120_000 ? latest : null);

  const beatMs = agent.lastBeatAt ? now - new Date(agent.lastBeatAt).getTime() : null;
  const meta = STATE_META[agent.state];
  const stc = dark ? C.warn : meta.color;
  const tabs: { k: TabKey; label: string; badge: number; red?: boolean }[] = [
    { k: 'durum', label: 'Sağlık', badge: redCount, red: true },
    { k: 'hatalar', label: 'Hatalar', badge: useDb ? (health?.errors || []).filter((g) => g.countToday > 0).length : groups.length },
    { k: 'gunluk', label: 'Günlükler', badge: 0 },
    { k: 'zaman', label: 'Zaman çizelgesi', badge: 0 },
    { k: 'ayarlar', label: 'Ayarlar', badge: 0 },
    { k: 'islemler', label: 'İşlemler', badge: 0 },
  ];
  const qfDisabled = !!blockedWhy || !!live || sending || qf?.phase === 'running';
  const clock = (iso: string | null | undefined, sec = false) => (iso ? fmtClock(iso, tz, sec) : '');
  /** Bugünse SS:dd, değilse GG.AA SS:dd — eski bir rapor "bugün" gibi okunmasın. */
  const stamp = (iso: string | null | undefined) => {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    return now - t < 20 * 3600_000 ? clock(iso)
      : new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'tr-TR', { day: '2-digit', month: '2-digit', timeZone: tz }) + ' ' + clock(iso);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(5,8,12,.55)', animation: 'caaFade .14s ease both' }}>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(640px, 100vw)',
          background: 'var(--surface)', borderLeft: '1px solid var(--border2)',
          boxShadow: '-24px 0 60px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Başlık */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <span style={{ ...dotStyle(stc, 8), boxShadow: '0 0 0 3px ' + stc + '1f' }} />
            <span style={{ fontSize: '19px', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{agent.name}</span>
            <span style={pill(stc)}>{dark ? L(lang, 'Karanlık', 'Dark') : meta.label}</span>
            {agent.brands.map((b) => <span key={b} style={chip}>{brandLabel(b)}</span>)}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
              <button
                onClick={() => void startQuickFix()}
                disabled={qfDisabled}
                style={{
                  display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 15px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                  ...(qfDisabled
                    ? { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--placeholder)', cursor: 'not-allowed' }
                    : { background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', cursor: 'pointer' }),
                }}
              >
                <Icon name="auto_fix_high" size={18} />
                <span>Hızlı düzelt</span>
              </button>
              <button
                onClick={onClose}
                title="Kapat"
                style={{ width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border)', borderRadius: '9px', color: 'var(--dim)', cursor: 'pointer' }}
              >
                <Icon name="close" size={19} />
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {agent.extVersion ? (
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', fontVariantNumeric: 'tabular-nums',
                color: outdated ? C.warn : 'var(--dim)', background: outdated ? 'rgba(224,162,60,.12)' : 'var(--surface2)',
                border: '1px solid ' + (outdated ? 'rgba(224,162,60,.35)' : 'var(--border)'),
              }}>
                {'v' + agent.extVersion}
              </span>
            ) : null}
            {outdated && <span style={{ fontSize: '11px', fontWeight: 600, color: C.warn }}>{L(lang, newest + ' mevcut', newest + ' available')}</span>}
            {/* Ortam (T5, 2.3.0+): kurulum türü · tarayıcı · işletim sistemi — yoksa hiç basılmaz (§6). */}
            {health?.env && [installLabel(health.env.installType, lang), health.env.browser, health.env.os].filter(Boolean).map((x, i) => (
              <span key={i} style={{ display: 'contents' }}>
                <span style={{ width: '3px', height: '3px', borderRadius: '9px', background: 'var(--muted)' }} />
                <span style={{ fontSize: '11.5px', color: (health.env?.installType === 'development' || health.env?.installType === 'sideload') && i === 0 ? C.warn : 'var(--dim)' }}>{x}</span>
              </span>
            ))}
            {beatMs != null && (
              <>
                {agent.extVersion && <span style={{ width: '3px', height: '3px', borderRadius: '9px', background: 'var(--muted)' }} />}
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', color: dark ? C.warn : 'var(--dim)' }}>
                  <Icon name="favorite" size={14} />
                  {dark
                    ? L(lang, 'Heartbeat ' + fmtDur(beatMs, lang) + ' sessiz', 'Heartbeat silent ' + fmtDur(beatMs, lang))
                    : L(lang, 'Heartbeat ' + fmtDur(beatMs, lang) + ' önce', 'Heartbeat ' + fmtDur(beatMs, lang) + ' ago')}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Karanlık / kapalı panel. 2.3.0+: "bir sonraki açılışta uygula" (T2, 24 sa bekler). */}
        {(dark || (closed && toolkit)) && (
          <div style={{ margin: '14px 22px 0', padding: '13px 14px', background: 'rgba(224,162,60,.08)', border: '1px solid rgba(224,162,60,.3)', borderRadius: '11px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <Icon name="support" size={20} color={C.warn} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                  {!dark
                    ? L(lang, 'Panel kapalı', 'Panel closed')
                    : beatMs != null
                      ? L(lang, 'Panel açık görünüyor ama ' + fmtDur(beatMs, lang) + ' sessiz', 'Panel looks open but is silent for ' + fmtDur(beatMs, lang))
                      : L(lang, 'Panel açık görünüyor ama sessiz', 'Panel looks open but is silent')}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--dim)', lineHeight: 1.45, marginTop: '2px' }}>
                  {toolkit
                    ? L(lang, 'Komutlar şu an ulaşamaz. Temsilciyi arayın ya da komutu paneli bir sonraki açılışında çalışacak şekilde sıraya alın (24 saate kadar bekler).', 'Commands can’t reach it now. Call the agent, or queue a command to run when the panel next opens (waits up to 24 h).')
                    : L(lang, 'Komutlar ulaşamaz. Temsilciyi telefonla arayın.', 'Commands can’t reach it. Call the agent.')}
                </div>
              </div>
            </div>
            {toolkit && (
              <button
                onClick={() => setQueueNext(!queueNext)}
                style={{ alignSelf: 'flex-start', marginLeft: '30px', display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 11px', background: 'transparent', border: '1px solid var(--border2)', borderRadius: '8px', color: 'var(--text)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                <span style={{ width: '15px', height: '15px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', ...(queueNext ? { background: '#3d8bff', border: '1.5px solid #3d8bff' } : { border: '1.5px solid var(--muted)' }) }}>
                  {queueNext && <Icon name="check" size={13} color="#fff" />}
                </span>
                <span>Bir sonraki açılışta uygula</span>
              </button>
            )}
          </div>
        )}

        {/* Komut şeridi */}
        {stripCmd && <CommandStrip cmd={stripCmd} lang={lang} tz={tz} canCancel={cancellable(stripCmd)} onCancel={() => void cancel(stripCmd)} onRetry={() => request(stripCmd.kind, undefined, stripCmd.args && typeof stripCmd.args === 'object' ? stripCmd.args : {})} onDismiss={() => setDismissed(stripCmd.id)} retryDisabled={!!blockedWhy || !!live} />}

        {actionErr && (
          <div style={{ margin: '12px 22px 0', fontSize: '12px', color: 'var(--bad-text)' }}>{actionErr}</div>
        )}

        {/* Hızlı düzelt */}
        {qf && (
          <div style={{ margin: '14px 22px 0', background: INNER, border: '1px solid var(--accent-border)', borderRadius: '11px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '10px 13px', borderBottom: '1px solid var(--border)' }}>
              <Icon name="auto_fix_high" size={18} color={C.info} />
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', flex: 1 }}>
                {qf.phase === 'done' ? 'Hızlı düzelt · temiz' : 'Hızlı düzelt · merdiven yürütülüyor'}
              </span>
              <span style={{ fontSize: '11px', color: 'var(--faint)' }}>Önerir, siz tıklarsınız</span>
              <button onClick={() => setQf(null)} disabled={qf.phase === 'running'} style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'var(--faint)', cursor: qf.phase === 'running' ? 'not-allowed' : 'pointer' }}>
                <Icon name="close" size={16} />
              </button>
            </div>
            <div style={{ padding: '8px 13px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {qf.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12px' }}>
                  <Icon
                    name={s.state === 'done' ? 'check_circle' : s.state === 'fail' ? 'cancel' : 'progress_activity'}
                    size={16}
                    color={s.state === 'done' ? C.ok : s.state === 'fail' ? C.bad : C.info}
                    style={s.state === 'run' ? { animation: 'caaSpin 1s linear infinite' } : undefined}
                  />
                  <span style={{ width: '170px', flex: 'none', color: 'var(--text)', fontWeight: 500 }}>{kindLabel(s.kind, lang)}</span>
                  <span style={{ flex: 1, color: 'var(--dim)' }}>{s.text}</span>
                </div>
              ))}
              {qf.phase === 'suggest' && qf.suggestion && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px', padding: '9px 11px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '9px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', color: 'var(--faint)' }}>Önerilen sonraki adım</div>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>
                      {kindLabel(qf.suggestion.fix || '', lang)}
                      <span style={{ fontWeight: 400, color: 'var(--dim)' }}>{' — ' + qf.suggestion.label + ': ' + qf.suggestion.reason}</span>
                    </div>
                  </div>
                  <button onClick={runSuggestion} disabled={!!blockedWhy} style={btn('primary', !!blockedWhy)}>Çalıştır</button>
                </div>
              )}
              {(qf.phase === 'done' || qf.phase === 'blocked') && qf.note && (
                <div style={{ marginTop: '4px', fontSize: '12px', color: qf.phase === 'done' ? C.ok : C.warn, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name={qf.phase === 'done' ? 'check_circle' : 'error'} size={16} />
                  {qf.note}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sekmeler */}
        <div style={{ display: 'flex', gap: '2px', padding: '14px 22px 0', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              style={{
                display: 'flex', alignItems: 'center', padding: '9px 12px 11px', background: 'transparent', border: 'none',
                borderBottom: '2px solid ' + (tab === t.k ? 'var(--accent)' : 'transparent'), marginBottom: '-1px',
                color: tab === t.k ? 'var(--text)' : 'var(--dim)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <span>{t.label}</span>
              {t.badge > 0 && (
                <span style={{ marginLeft: '6px', minWidth: '17px', height: '17px', padding: '0 5px', borderRadius: '999px', background: t.red ? C.bad : 'var(--border2)', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px 24px' }}>
          {!health && !loadErr && <div style={{ fontSize: '12px', color: 'var(--faint)' }}>Okunuyor…</div>}
          {loadErr && !health && <div style={{ fontSize: '12px', color: 'var(--bad-text)' }}>{L(lang, 'Okunamadı: ' + loadErr, 'Could not load: ' + loadErr)}</div>}

          {health && tab === 'durum' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '11.5px', color: 'var(--faint)', flex: 1 }}>
                  {health.diagnostics?.at && diagFresh
                    ? L(lang, 'Durum raporu ' + clock(health.diagnostics.at) + ' · sayaçlar 00:00\'dan beri', 'From diagnostics at ' + clock(health.diagnostics.at) + ' · counters since 00:00')
                    : health.diagnostics?.at
                      ? L(lang, 'Son durum raporu ' + stamp(health.diagnostics.at) + ' · 2 saatten eski, kullanılmadı', 'Last diagnostics ' + stamp(health.diagnostics.at) + ' · older than 2 h, not used')
                      : L(lang, 'Henüz durum raporu yok · sayaçlar 00:00\'dan beri', 'No diagnostics yet · counters since 00:00')}
                </span>
                {(() => { const b = actBtn('diagnostics', L(lang, 'Durum raporu al', 'Run diagnostics')); return <button onClick={b.onClick} disabled={b.disabled} style={b.style}>{b.label}</button>; })()}
              </div>
              <div style={{ background: INNER, border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
                {rows.map((h) => {
                  const dashed = h.level === 'none';
                  const fb = h.fix ? actBtn(h.fix, kindLabel(h.fix, lang), h.level === 'bad') : null;
                  return (
                    <div key={h.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px', borderBottom: '1px solid ' + ROWLINE }}>
                      <span style={dashed ? { width: '9px', height: '9px', borderRadius: '999px', flex: 'none', border: '1.5px dashed var(--faint)' } : dotStyle(levelColor(h.level), 9)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>{h.label}</div>
                        <div style={{ fontSize: '11.5px', marginTop: '1px', color: dashed ? 'var(--faint)' : h.level === 'bad' ? 'var(--bad-text)' : 'var(--dim)' }}>{h.reason}</div>
                      </div>
                      {fb && (h.level === 'bad' || h.level === 'warn') && <button onClick={fb.onClick} disabled={fb.disabled} style={fb.style}>{fb.label}</button>}
                      {h.note && <span style={{ fontSize: '11px', color: 'var(--faint)', textAlign: 'right', maxWidth: '160px', lineHeight: 1.35 }}>{h.note}</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '11px', color: 'var(--faint)', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={dotStyle(C.ok, 8)} /><span>Sorun yok</span></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={dotStyle(C.warn, 8)} /><span>İzle</span></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={dotStyle(C.bad, 8)} /><span>Hemen düzelt</span></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '8px', height: '8px', borderRadius: '9px', border: '1.5px dashed var(--faint)' }} /><span>Ölçülmedi</span></span>
              </div>
            </>
          )}

          {health && tab === 'hatalar' && useDb && (
            <>
              {/* Raporlar (T4) — temsilcinin "Sorun bildir"i ve admin anlık görüntüleri. */}
              {(health.reports?.length ?? 0) > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>Raporlar</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(health.reports || []).slice(0, 5).map((r) => {
                      const m = REPORT_STATUS_META[r.status];
                      return (
                        <button key={r.id} onClick={() => setReportId(r.id)} style={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'grid', gridTemplateColumns: '84px minmax(0,1fr) auto', gap: '10px', alignItems: 'center', padding: '8px 12px', background: INNER, border: '1px solid var(--border)', borderRadius: '10px' }}>
                          <span style={{ fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{stamp(r.createdAt)}</span>
                          <span style={{ fontSize: '12.5px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {(r.source === 'agent' ? L(lang, 'Sorun bildirdi', 'Reported a problem') : L(lang, 'Anlık görüntü', 'Snapshot')) + (r.note ? ' · “' + r.note + '”' : '') + (r.imageCount ? ' · ' + L(lang, r.imageCount + ' görüntü', r.imageCount + (r.imageCount === 1 ? ' image' : ' images')) : '')}
                          </span>
                          <span style={pill(m.color)}>{L(lang, m.tr, m.en)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {(['today', '7d'] as const).map((k) => (
                  <button key={k} onClick={() => setErrRange(k)} style={seg(errRange === k)}>{k === 'today' ? 'Bugün' : '7 gün'}</button>
                ))}
                <span style={{ width: '1px', height: '18px', background: 'var(--border)', margin: '0 4px' }} />
                {(['all', 'error', 'warn'] as const).map((k) => (
                  <button key={k} onClick={() => setErrLevel(k)} style={seg(errLevel === k)}>
                    {k === 'all' ? 'Tümü' : k === 'error' ? 'Yalnız hatalar' : 'Yalnız uyarılar'}
                  </button>
                ))}
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--faint)' }}>Parmak izine göre gruplu · 30 gün saklanır</span>
                {(() => { const b = actBtn('snapshot', L(lang, 'Anlık görüntü al', 'Take snapshot')); return <button onClick={b.onClick} disabled={b.disabled} style={b.style}>{b.label}</button>; })()}
              </div>
              {dbGroups.filter((g) => errLevel === 'all' || g.level === errLevel).length === 0 ? (
                <EmptyNote text={errRange === 'today' ? L(lang, 'Bugün eklentiden hata/uyarı gelmedi.', 'No errors or warnings from the extension today.') : L(lang, 'Son 7 günde eklentiden hata/uyarı gelmedi.', 'No errors or warnings from the extension in the last 7 days.')} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {dbGroups.filter((g) => errLevel === 'all' || g.level === errLevel).map((g) => (
                    <DbErrorCard key={g.fp} g={g} count={errRange === 'today' ? g.countToday : g.count7d} lines={pushed.filter((l) => l.fp === g.fp)} stamp={stamp} tz={tz} />
                  ))}
                </div>
              )}
            </>
          )}

          {health && tab === 'hatalar' && !useDb && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {(['all', 'error', 'warn'] as const).map((k) => (
                  <button key={k} onClick={() => setErrLevel(k)} style={seg(errLevel === k)}>
                    {k === 'all' ? 'Tümü' : k === 'error' ? 'Yalnız hatalar' : 'Yalnız uyarılar'}
                  </button>
                ))}
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--faint)' }}>
                  {health.logs?.at
                    ? L(lang, 'Son günlük çekimi ' + stamp(health.logs.at) + ' · parmak izine göre gruplu', 'Last log pull ' + stamp(health.logs.at) + ' · grouped by fingerprint')
                    : ''}
                </span>
                <LogsButton actBtn={actBtn} lang={lang} />
              </div>
              {groups.length === 0 ? (
                <EmptyNote text={health.logs ? L(lang, 'Son çekimde uyarı/hata satırı yok.', 'No warn/error lines in the last pull.') : L(lang, 'Henüz günlük çekilmedi — "Günlükleri al" çalıştırın.', 'No logs fetched yet — run Fetch logs.')} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {groups.filter((g) => errLevel === 'all' || g.level === errLevel).map((g) => <ErrorGroupCard key={g.key} g={g} />)}
                </div>
              )}
            </>
          )}

          {health && tab === 'gunluk' && toolkit && (
            <PushedLogsTab
              lines={paused || pushed}
              paused={!!paused}
              onPause={() => setPaused(paused ? null : pushed)}
              cat={logCat}
              setCat={setLogCat}
              debugUntil={debugUntil}
              debugBtn={actBtn('debug_logging', '', false, { minutes: debugUntil ? 0 : 15 })}
              live={!paused && !dark && !closed}
              tz={tz}
              lang={lang}
              agentName={agent.name}
            />
          )}

          {health && tab === 'gunluk' && !toolkit && (
            <LogsTab
              lines={logLines}
              at={health.logs?.at ? stamp(health.logs.at) : ''}
              raw={health.logs?.lines || []}
              cat={logCat}
              setCat={setLogCat}
              actBtn={actBtn}
              lang={lang}
              agentName={agent.name}
            />
          )}

          {health && tab === 'zaman' && <TimelineTab health={health} tz={tz} lang={lang} onOpenReport={setReportId} />}

          {health && tab === 'ayarlar' && <SettingsTab env={health.env || null} debugUntil={debugUntil} tz={tz} diag={diagAny} at={health.diagnostics?.at ? stamp(health.diagnostics.at) : ''} actBtn={actBtn} lang={lang} />}

          {health && tab === 'islemler' && (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '10px', gap: '10px' }}>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>Onarım merdiveni</span>
                <span style={{ fontSize: '11px', color: 'var(--faint)' }}>Kademe 1 tek tık · kademe 2 onay · kademe 3 adını yazın</span>
              </div>
              <div style={{ background: INNER, border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', marginBottom: '12px' }}>
                {LADDER.map((s) => {
                  const b = actBtn(s.kind, L(lang, 'Çalıştır', 'Run'), s.kind === 'flush_wal' && rows.some((r) => r.key === 'wal' && r.level === 'bad'));
                  const tier = confirmTier(s.kind);
                  return (
                    <div key={s.kind} style={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr) auto', gap: '12px', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid ' + ROWLINE }}>
                      <span style={{ fontSize: '12px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{s.n}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{(LADDER_TEXT[s.kind] || [kindLabel(s.kind, lang)])[0]}</span>
                          <span style={tierStyle(tier)}>{L(lang, 'K', 'T') + tier}</span>
                          {s.isNew && <span style={{ fontSize: '9.5px', fontWeight: 700, color: 'var(--accent-text)', background: 'rgba(61,139,255,.12)', border: '1px solid rgba(61,139,255,.35)', borderRadius: '5px', padding: '1px 5px' }}>YENİ</span>}
                        </div>
                        <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '2px' }}>{(LADDER_TEXT[s.kind] || ['', ''])[1]}</div>
                      </div>
                      <button onClick={b.onClick} disabled={b.disabled} style={b.style}>{b.label}</button>
                    </div>
                  );
                })}
              </div>
              <button onClick={onOpenAllCommands} style={{ ...btn('ghost'), display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
                <Icon name="support_agent" size={16} />
                <span>Tüm komutlar (mesaj, sayfa, mola, kuyruk…)</span>
              </button>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', marginBottom: '10px' }}>Komut geçmişi</div>
              {health.commands.length === 0 ? (
                <EmptyNote text={L(lang, 'Bu temsilciye henüz komut gönderilmedi.', 'No commands sent to this agent yet.')} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {health.commands.map((c) => {
                    const sb = statusBadge(c.status, lang);
                    const at = c.done_at || c.issued_at;
                    const today = now - new Date(at).getTime() < 20 * 3600_000;
                    return (
                      <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '70px minmax(0,1fr) auto', gap: '12px', alignItems: 'center', padding: '9px 12px', background: INNER, border: '1px solid var(--border)', borderRadius: '10px' }}>
                        <span style={{ fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                          {today ? clock(at) : new Date(at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'tr-TR', { day: '2-digit', month: '2-digit', timeZone: tz }) + ' ' + clock(at)}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500 }}>{kindLabel(c.kind, lang)}</div>
                          <div style={{ fontSize: '11.5px', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summarizeResult(c.kind, c.status, c.result, lang, tz)}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                          <span style={pill(levelColor(sb.level))}>{sb.text}</span>
                          {c.issued_by && <span style={{ fontSize: '10.5px', color: 'var(--faint)' }}>{c.issued_by}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {reportId && <ExtReportModal id={reportId} lang={lang} onClose={() => setReportId(null)} onChanged={() => void load()} />}

        {/* Onay */}
        {confirm && (
          <div onClick={() => setConfirm(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(5,8,12,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: '400px', maxWidth: '100%', background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: '14px', padding: '20px', boxShadow: '0 24px 60px rgba(0,0,0,.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
                <span style={tierStyle(confirm.tier)}>{L(lang, 'K', 'T') + confirm.tier}</span>
                <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{kindLabel(confirm.kind, lang) + '?'}</span>
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--dim)', lineHeight: 1.5, marginBottom: '14px' }}>
                {(LADDER_TEXT[confirm.kind]?.[1] ? LADDER_TEXT[confirm.kind][1] + ' ' : '')}
                {L(lang, agent.name + ' paneline gider; gönderen olarak siz kaydedilirsiniz.', 'Runs on ' + agent.name + '’s panel; you are recorded as the sender.')}
              </div>
              {confirm.tier === 3 && (
                <>
                  <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginBottom: '6px' }}>
                    {L(lang, 'Onaylamak için yazın: ', 'Type to confirm: ')}
                    <span style={{ fontFamily: MONO, color: 'var(--text)' }}>{confirm.kind}</span>
                  </div>
                  <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={confirm.kind} style={{ ...inputStyle, fontFamily: MONO, marginBottom: '14px' }} />
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => setConfirm(null)} style={{ ...btn('ghost'), color: 'var(--text)', borderColor: 'var(--border2)', padding: '9px 14px' }}>Vazgeç</button>
                {(() => {
                  const blocked = confirm.tier === 3 && typed.trim() !== confirm.kind;
                  return (
                    <button
                      onClick={() => { if (!blocked) confirm.go(); }}
                      disabled={blocked}
                      style={{ padding: '9px 14px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, border: 'none', ...(blocked ? { background: 'var(--border)', color: 'var(--placeholder)', cursor: 'not-allowed' } : { background: confirm.tier === 3 ? C.bad : C.info, color: '#fff', cursor: 'pointer' }) }}
                    >
                      Komutu gönder
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type ActBtn = (kind: string, label: string, primary?: boolean) => { label: string; disabled: boolean; style: React.CSSProperties; onClick: (() => void) | undefined };

function LogsButton({ actBtn, lang }: { actBtn: ActBtn; lang: Lang }) {
  const b = actBtn('tail_logs', L(lang, 'Günlükleri al', 'Fetch logs'));
  return <button onClick={b.onClick} disabled={b.disabled} style={b.style}>{b.label}</button>;
}

function EmptyNote({ text }: { text: string }) {
  return <div style={{ padding: '18px 14px', background: INNER, border: '1px dashed var(--border2)', borderRadius: '12px', fontSize: '12px', color: 'var(--faint)' }}>{text}</div>;
}

function CommandStrip({ cmd, lang, tz, canCancel, onCancel, onRetry, onDismiss, retryDisabled }: {
  cmd: AgentCommand; lang: Lang; tz: string; canCancel: boolean; onCancel: () => void; onRetry: () => void; onDismiss: () => void; retryDisabled: boolean;
}) {
  const st = cmd.status;
  const busy = isLiveStatus(st);
  const refused = st === 'refused' && (cmd.result as { reason?: string } | null)?.reason === 'agent_entering_result';
  const color = refused || st === 'refused' || st === 'unsupported' ? C.warn : st === 'done' ? C.ok : st === 'failed' ? C.bad : st === 'expired' ? C.none : C.info;
  const order = ['pending', 'delivered', 'done'];
  const idx = st === 'accepted' ? 1 : order.indexOf(st);
  const by = cmd.issued_by;
  const at = fmtClock(cmd.issued_at, tz);
  return (
    <div style={{ margin: '14px 22px 0', padding: '11px 13px', background: color + '12', border: '1px solid ' + color + '4d', borderRadius: '11px', display: 'flex', alignItems: 'center', gap: '11px' }}>
      <Icon
        name={refused ? 'front_hand' : st === 'done' ? 'check_circle' : busy ? 'progress_activity' : st === 'failed' ? 'cancel' : 'info'}
        size={20}
        color={color}
        style={busy ? { animation: 'caaSpin 1s linear infinite' } : undefined}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>
          {refused ? 'Temsilci sonuç giriyor — birazdan tekrar deneyin' : kindLabel(cmd.kind, lang) + ' · ' + statusBadge(st, lang).text}
        </div>
        <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '1px' }}>
          {busy && cmd.deferred && !cmd.accepted_at
            ? L(lang, 'Panelin bir sonraki açılışında çalışacak · ' + (by ? by + ' ' : '') + at + ' · 24 saate kadar bekler', 'Runs when the panel next opens · ' + (by ? by + ' ' : '') + at + ' · waits up to 24 h')
            : busy
            ? L(lang, (by ? by + ' gönderdi ' : 'Gönderildi ') + at + ' · bu bitene kadar diğer komutlar bekler', 'Sent' + (by ? ' by ' + by : '') + ' ' + at + ' · other commands wait until this one finishes')
            : summarizeResult(cmd.kind, st, cmd.result, lang) + (by ? ' · ' + by + ' ' + at : '')}
        </div>
      </div>
      {busy && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {order.map((o, i) => (
            <span key={o} style={{ fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '999px', ...(i <= idx ? { color, background: color + '1a', border: '1px solid ' + color + '40' } : { color: 'var(--placeholder)', border: '1px solid var(--border)' }) }}>
              {o === 'pending' ? L(lang, 'Gönderildi', 'Sent') : o === 'delivered' ? L(lang, 'Ulaştı', 'Delivered') : L(lang, 'Bitti', 'Done')}
            </span>
          ))}
        </div>
      )}
      {busy && canCancel && <button onClick={onCancel} style={{ ...btn('ghost'), color: 'var(--text)', borderColor: 'var(--border2)' }}>İptal</button>}
      {refused && (
        <button onClick={onRetry} disabled={retryDisabled} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 11px', background: retryDisabled ? 'transparent' : C.warn, border: retryDisabled ? '1px solid var(--border)' : 'none', borderRadius: '8px', color: retryDisabled ? 'var(--placeholder)' : '#1a1206', fontSize: '12px', fontWeight: 700, cursor: retryDisabled ? 'not-allowed' : 'pointer' }}>
          <Icon name="refresh" size={15} />
          <span>Tekrar dene</span>
        </button>
      )}
      {!busy && (
        <button onClick={onDismiss} style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'var(--faint)', cursor: 'pointer' }}>
          <Icon name="close" size={17} />
        </button>
      )}
    </div>
  );
}

function ErrorGroupCard({ g }: { g: ReturnType<typeof groupErrors>[number] }) {
  const [open, setOpen] = useState(false);
  const c = g.level === 'error' ? C.bad : C.warn;
  return (
    <div style={{ background: INNER, border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '12px', padding: '12px 14px', cursor: 'pointer' }}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <span style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: c, background: c + '1a', border: '1px solid ' + c + '40', borderRadius: '5px', padding: '1px 6px', flex: 'none' }}>
              {g.level === 'error' ? 'error' : 'warn'}
            </span>
            {g.cat && <span style={{ fontFamily: MONO, fontSize: '11px', color: 'var(--accent-text)', flex: 'none' }}>{'[' + g.cat + ']'}</span>}
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.message}</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>
            <span>{g.first}</span><span>→</span><span>{g.last}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{g.count}</span>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={20} color="var(--faint)" />
        </div>
      </div>
      {open && (
        <div style={{ borderTop: '1px solid ' + ROWLINE, background: 'var(--inset2)', padding: '6px 14px 10px' }}>
          {g.lines.slice().reverse().map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '62px minmax(0,1fr)', gap: '10px', padding: '4px 0', fontSize: '11px', fontFamily: MONO }}>
              <span style={{ color: 'var(--faint)' }}>{l.t}</span>
              <span style={{ color: 'var(--dim)', wordBreak: 'break-word' }}>{l.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LogsTab({ lines, at, raw, cat, setCat, actBtn, lang, agentName }: {
  lines: LogLine[]; at: string; raw: string[]; cat: string; setCat: (c: string) => void;
  actBtn: ActBtn; lang: Lang; agentName: string;
}) {
  const parsed = lines;
  const cats = ['all', ...Array.from(new Set(parsed.map((l) => l.cat).filter(Boolean)))];
  const shown = parsed.filter((l) => cat === 'all' || l.cat === cat).slice().reverse();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(raw.join('\n')); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch { /* izin yok */ }
  };
  const download = () => {
    const blob = new Blob([raw.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'eklenti-gunluk-' + agentName.toLowerCase().replace(/[^a-z0-9ğüşıöç]+/gi, '-') + '.txt';
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const smallBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--dim)', fontSize: '11.5px', fontWeight: 600, cursor: raw.length ? 'pointer' : 'not-allowed' };
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {cats.map((k) => (
          <button key={k} onClick={() => setCat(k)} style={seg(cat === k)}>{k === 'all' ? 'Tümü' : '[' + k + ']'}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button onClick={() => void copy()} disabled={!raw.length} style={smallBtn}><Icon name={copied ? 'check' : 'content_copy'} size={15} /><span>{copied ? 'Kopyalandı' : 'Kopyala'}</span></button>
          <button onClick={download} disabled={!raw.length} style={smallBtn}><Icon name="download" size={15} /><span>.txt</span></button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '10px', marginBottom: '10px' }}>
        <Icon name="history" size={18} color="var(--dim)" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>
            {at ? L(lang, 'Son çekim ' + at + ' · ' + raw.length + ' satır', 'Last pull ' + at + ' · ' + raw.length + ' lines') : L(lang, 'Henüz günlük çekilmedi', 'No logs fetched yet')}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--dim)' }}>Yalnız uyarı ve hata satırları · panel kapanınca halka silinir</div>
        </div>
        <LogsButton actBtn={actBtn} lang={lang} />
      </div>
      {shown.length > 0 ? (
        <div style={{ background: 'var(--inset2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '8px 0', fontFamily: MONO, fontSize: '11.5px' }}>
          {shown.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '62px 44px 84px minmax(0,1fr)', gap: '8px', padding: '3px 12px', lineHeight: 1.5 }}>
              <span style={{ color: 'var(--faint)' }}>{l.t}</span>
              <span style={{ color: l.level === 'error' ? C.bad : C.warn, fontWeight: 600 }}>{l.level}</span>
              <span style={{ color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.cat ? '[' + l.cat + ']' : ''}</span>
              <span style={{ color: 'var(--code)', wordBreak: 'break-word' }}>{l.msg}</span>
            </div>
          ))}
        </div>
      ) : (
        raw.length > 0 && <EmptyNote text={L(lang, 'Bu kategoride satır yok.', 'No lines in this category.')} />
      )}
      <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '8px' }}>Rakam dizileri temsilcinin tarayıcısından çıkmadan ##### olarak maskelenir.</div>
    </>
  );
}

/** Faz 2 hata grubu (ext_log_lines, sunucuda gruplu). Açınca son 24 saatin aynı parmak izli satırları. */
function DbErrorCard({ g, count, lines, stamp, tz }: {
  g: ExtErrorGroup; count: number; lines: ExtLogLine[]; stamp: (iso: string | null | undefined) => string; tz: string;
}) {
  const [open, setOpen] = useState(false);
  const c = g.level === 'error' ? C.bad : C.warn;
  return (
    <div style={{ background: INNER, border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '12px', padding: '12px 14px', cursor: 'pointer' }}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <span style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: c, background: c + '1a', border: '1px solid ' + c + '40', borderRadius: '5px', padding: '1px 6px', flex: 'none' }}>
              {g.level === 'error' ? 'error' : 'warn'}
            </span>
            {g.cat && <span style={{ fontFamily: MONO, fontSize: '11px', color: 'var(--accent-text)', flex: 'none' }}>{'[' + g.cat + ']'}</span>}
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.message}</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums', flexWrap: 'wrap' }}>
            <span>{stamp(g.first)}</span><span>→</span><span>{stamp(g.last)}</span>
            {g.versions.length > 0 && <span>{g.versions.map((v) => 'v' + v).join(', ')}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={20} color="var(--faint)" />
        </div>
      </div>
      {open && (
        <div style={{ borderTop: '1px solid ' + ROWLINE, background: 'var(--inset2)', padding: '6px 14px 10px' }}>
          {lines.length === 0 ? (
            <div style={{ fontSize: '11px', color: 'var(--faint)', padding: '4px 0' }}>Son 24 saatte satır yok.</div>
          ) : lines.slice(0, 12).map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '62px 50px minmax(0,1fr)', gap: '10px', padding: '4px 0', fontSize: '11px', fontFamily: MONO }}>
              <span style={{ color: 'var(--faint)' }}>{fmtClock(l.at, tz, true)}</span>
              <span style={{ color: 'var(--faint)' }}>{l.version ? 'v' + l.version : ''}</span>
              <span style={{ color: 'var(--dim)', wordBreak: 'break-word' }}>{l.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Faz 2 Günlükler: eklentinin GÖNDERDİĞİ satırlar (warn/error her zaman, info yalnız hata ayıklama modunda). */
function PushedLogsTab({ lines, paused, onPause, cat, setCat, debugUntil, debugBtn, live, tz, lang, agentName }: {
  lines: ExtLogLine[]; paused: boolean; onPause: () => void; cat: string; setCat: (c: string) => void;
  debugUntil: number | null; debugBtn: ReturnType<ActBtn>; live: boolean; tz: string; lang: Lang; agentName: string;
}) {
  const cats = ['all', ...Array.from(new Set(lines.map((l) => l.cat).filter(Boolean)))];
  const shown = lines.filter((l) => cat === 'all' || l.cat === cat);
  const [copied, setCopied] = useState(false);
  const text = () => shown.slice().reverse().map((l) => fmtClock(l.at, tz, true) + ' ' + l.level.toUpperCase().slice(0, 1) + ' ' + (l.cat ? '[' + l.cat + '] ' : '') + l.msg).join('\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text()); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch { /* izin yok */ }
  };
  const download = () => {
    const blob = new Blob([text() + '\n'], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'eklenti-gunluk-' + agentName.toLowerCase().replace(/[^a-z0-9ğüşıöç]+/gi, '-') + '.txt';
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const smallBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--dim)', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer' };
  const on = debugUntil != null;
  const dis = debugBtn.disabled;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {cats.map((k) => (
          <button key={k} onClick={() => setCat(k)} style={seg(cat === k)}>{k === 'all' ? 'Tümü' : '[' + k + ']'}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button onClick={onPause} style={smallBtn}><Icon name={paused ? 'play_arrow' : 'pause'} size={15} /><span>{paused ? 'Devam' : 'Duraklat'}</span></button>
          <button onClick={() => void copy()} disabled={!shown.length} style={smallBtn}><Icon name={copied ? 'check' : 'content_copy'} size={15} /><span>{copied ? 'Kopyalandı' : 'Kopyala'}</span></button>
          <button onClick={download} disabled={!shown.length} style={smallBtn}><Icon name="download" size={15} /><span>.txt</span></button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '10px', marginBottom: '10px' }}>
        <button
          onClick={debugBtn.onClick}
          disabled={dis}
          title={dis ? debugBtn.label : undefined}
          style={{ width: '36px', height: '20px', borderRadius: '999px', border: 'none', position: 'relative', flex: 'none', cursor: dis ? 'not-allowed' : 'pointer', background: on && !dis ? C.info : 'var(--border2)', opacity: dis ? 0.5 : 1 }}
        >
          <span style={{ position: 'absolute', top: '2px', left: on && !dis ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '999px', background: '#fff', transition: 'left .15s' }} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>Hata ayıklama modu · 15 dk</div>
          <div style={{ fontSize: '11px', color: 'var(--dim)' }}>
            {on
              ? L(lang, 'Bilgi satırları da geliyor · kendiliğinden kapanış ' + fmtClock(new Date(debugUntil as number).toISOString(), tz), 'Streaming info lines too · switches itself off at ' + fmtClock(new Date(debugUntil as number).toISOString(), tz))
              : L(lang, 'Yalnız uyarı ve hata satırları. Açılırsa bilgi satırları da gelir, 15 dk sonra kendiliğinden kapanır.', 'Warn and error lines only. On: info lines too, off by itself after 15 min.')}
          </div>
        </div>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: live ? C.ok : 'var(--faint)' }}>
          <span style={{ ...dotStyle(live ? C.ok : 'var(--faint)', 7), animation: live ? 'caaPulse 2s ease-in-out infinite' : 'none' }} />
          {live ? L(lang, 'Canlı', 'Live') : paused ? L(lang, 'Duraklatıldı', 'Paused') : L(lang, 'Sinyal yok', 'No signal')}
        </span>
      </div>
      {shown.length === 0 ? (
        <EmptyNote text={L(lang, 'Son 24 saatte eklentiden satır gelmedi.', 'No lines from the extension in the last 24 h.')} />
      ) : (
        <div style={{ background: 'var(--inset2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '8px 0', fontFamily: MONO, fontSize: '11.5px' }}>
          {shown.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '62px 44px 84px minmax(0,1fr)', gap: '8px', padding: '3px 12px', lineHeight: 1.5 }}>
              <span style={{ color: 'var(--faint)' }}>{fmtClock(l.at, tz, true)}</span>
              <span style={{ color: l.level === 'error' ? C.bad : l.level === 'warn' ? C.warn : 'var(--faint)', fontWeight: 600 }}>{l.level}</span>
              <span style={{ color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.cat ? '[' + l.cat + ']' : ''}</span>
              <span style={{ color: 'var(--code)', wordBreak: 'break-word' }}>{l.msg}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '8px' }}>Rakam dizileri temsilcinin tarayıcısından çıkmadan ##### olarak maskelenir.</div>
    </>
  );
}

function TimelineTab({ health, tz, lang, onOpenReport }: { health: ExtHealth; tz: string; lang: Lang; onOpenReport: (id: string) => void }) {
  type Ev = { at: string; icon: string; color: string; title: string; titleIsKey?: boolean; sub: string; reportId?: string };
  const startOfToday = Date.now() - 20 * 3600_000;
  const evs: Ev[] = [];
  for (const e of health.timeline) {
    if (e.type === 'panel_open') evs.push({ at: e.at, icon: 'power_settings_new', color: C.ok, title: L(lang, 'Panel açıldı', 'Panel opened'), sub: e.version ? 'v' + e.version : '' });
    else if (e.type === 'version') evs.push({ at: e.at, icon: 'upgrade', color: '#9aa4b2', title: L(lang, 'Sürüm ilk kez görüldü: v' + e.version, 'Version first seen: v' + e.version), sub: '' });
    else if (e.type === 'report' && e.id) evs.push({ at: e.at, icon: e.source === 'admin' ? 'photo_camera' : 'flag', color: C.info, title: e.source === 'admin' ? L(lang, 'Anlık görüntü alındı', 'Snapshot taken') : L(lang, 'Temsilci sorun bildirdi', 'Agent reported a problem'), sub: e.note ? '“' + e.note + '”' : '', reportId: e.id });
    else if (e.type === 'alert' && e.rule) evs.push({ at: e.at, icon: 'notifications', color: e.severity === 'critical' ? C.bad : C.warn, title: RULE_TITLES[e.rule] || e.rule, titleIsKey: true, sub: e.description || '' });
  }
  for (const c of health.commands) {
    const at = c.done_at || c.issued_at;
    if (new Date(at).getTime() < startOfToday) continue;
    const sb = statusBadge(c.status, lang);
    evs.push({ at, icon: c.status === 'refused' ? 'front_hand' : 'terminal', color: levelColor(sb.level), title: kindLabel(c.kind, lang) + ' · ' + sb.text.toLowerCase(), sub: summarizeResult(c.kind, c.status, c.result, lang) + (c.issued_by ? ' · ' + c.issued_by : '') });
  }
  evs.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  if (!evs.length) return <EmptyNote text={L(lang, 'Bugün kaydedilmiş olay yok.', 'No events recorded today.')} />;
  return (
    <>
      <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '12px' }}>Bugün · panel açılışları, sürüm, uyarılar ve komutlar</div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {evs.map((e, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '46px 28px minmax(0,1fr)', gap: '8px', alignItems: 'stretch' }}>
            <span style={{ fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', paddingTop: '4px' }}>{fmtClock(e.at, tz)}</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', color: e.color, background: e.color + '1a', border: '1px solid ' + e.color + '40' }}>
                <Icon name={e.icon} size={14} />
              </span>
              {i < evs.length - 1 && <span style={{ flex: 1, width: '1px', background: 'var(--border)', minHeight: '10px' }} />}
            </div>
            <div style={{ padding: '3px 0 16px', minWidth: 0, cursor: e.reportId ? 'pointer' : 'default' }} onClick={e.reportId ? () => onOpenReport(e.reportId as string) : undefined}>
              <div style={{ fontSize: '12.5px', color: e.reportId ? 'var(--accent-text)' : 'var(--text)', fontWeight: 500 }}>{e.title}</div>
              {e.sub && <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '2px' }}>{e.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function SettingsTab({ env, debugUntil, tz, diag, at, actBtn, lang }: {
  env: { installType: string; browser: string; os: string } | null; debugUntil: number | null; tz: string;
  diag: Record<string, unknown> | null; at: string; actBtn: ActBtn; lang: Lang;
}) {
  if (!diag && !env) {
    return (
      <>
        <EmptyNote text={L(lang, 'Henüz durum raporu yok — eklentinin sakladıkları durum raporuyla gelir.', 'No diagnostics yet — what the extension stores comes with Diagnostics.')} />
        <div style={{ marginTop: '10px' }}>{(() => { const b = actBtn('diagnostics', L(lang, 'Durum raporu al', 'Run diagnostics')); return <button onClick={b.onClick} disabled={b.disabled} style={b.style}>{b.label}</button>; })()}</div>
      </>
    );
  }
  const d = diag || {};
  const s = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const n = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : null);
  const b = (k: string) => (typeof d[k] === 'boolean' ? (d[k] as boolean) : null);
  const yes = (v: boolean | null) => (v == null ? '' : v ? L(lang, 'Evet', 'Yes') : L(lang, 'Hayır', 'No'));
  const phone = d.phone && typeof d.phone === 'object' ? (d.phone as Record<string, unknown>) : null;
  const nav = d.nav && typeof d.nav === 'object' ? (d.nav as Record<string, unknown>) : null;
  const queue = n('queueCount');
  const rejected = rejectedRowOf(d.rejectedRow);
  const total = n('sheetTotal');
  type Row = { k: string; v: string; fix?: [string, string]; lock?: string };
  const rows: Row[] = [
    { k: 'Aktif sayfa', v: s('sheet'), fix: ['reset_queue_cursor', L(lang, 'İmleci sıfırla', 'Reset cursor')] },
    { k: 'Marka', v: s('brandKey') },
    { k: 'Dil', v: s('lang') },
    { k: 'Duraklatıldı', v: yes(b('paused')) },
    { k: 'Sıra', v: queue != null ? (total != null ? queue + ' / ' + total : String(queue)) : '' },
    { k: 'Bugün biten', v: n('todayDone') != null ? String(n('todayDone')) : '', fix: ['refresh_stats', L(lang, 'Yenile', 'Refresh')] },
    { k: 'Panel önbelleği', v: n('qcacheRows') != null ? L(lang, n('qcacheRows') + ' satır', n('qcacheRows') + ' rows') : '', fix: ['clear_cache', L(lang, 'Temizle', 'Clear')] },
    { k: 'SMS şablonları', v: '', fix: ['clear_sms_cache', L(lang, 'Yenile', 'Refresh')] },
    { k: 'Atlanan satır', v: rejected ? fmtRejectedRow(rejected) : '', fix: rejected ? ['clear_rejected_rows', L(lang, 'Temizle', 'Clear')] : undefined },
    { k: 'Bekleyen kuyruk', v: n('walTotal') != null ? L(lang, n('walTotal') + ' sonuç', n('walTotal') + ' results') : '', lock: L(lang, 'Burada sıfırlanmaz', 'Never reset here') },
    { k: 'Softphone', v: phone && typeof phone.status === 'string' ? phone.status : '' },
    { k: 'Sayfa geçişi (SPA)', v: nav ? yes(typeof nav.spa === 'boolean' ? nav.spa : null) : '' },
    { k: 'Bellek', v: nav && typeof nav.heapMb === 'number' ? nav.heapMb + ' MB' : '' },
    { k: 'Sekme görünür', v: yes(b('tabVisible')) },
    // T5 (2.3.0+): kurulum ortamı — census satırından, durum raporu gerekmez.
    { k: 'Kurulum', v: env ? installLabel(env.installType, lang) : '' },
    { k: 'Tarayıcı', v: env?.browser || '' },
    { k: 'İşletim sistemi', v: env?.os || '' },
    { k: 'Hata ayıklama', v: debugUntil ? L(lang, 'Açık · kapanış ' + fmtClock(new Date(debugUntil).toISOString(), tz), 'On until ' + fmtClock(new Date(debugUntil).toISOString(), tz)) : (env ? L(lang, 'Kapalı', 'Off') : '') },
  ];
  return (
    <>
      <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '10px' }}>
        {L(lang, 'Eklentinin sakladıkları · salt okunur · durum raporu ' + at, 'What the extension has stored · read-only · from diagnostics at ' + at)}
      </div>
      <div style={{ background: INNER, border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
        {rows.map((r) => {
          const fb = r.fix ? actBtn(r.fix[0], r.fix[1]) : null;
          return (
            <div key={r.k} style={{ display: 'grid', gridTemplateColumns: '170px minmax(0,1fr) auto', gap: '12px', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid ' + ROWLINE }}>
              <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{r.k}</span>
              <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.v}</span>
              {fb ? <button onClick={fb.onClick} disabled={fb.disabled} style={fb.style}>{fb.label}</button>
                : r.lock ? <span style={{ fontSize: '11px', color: 'var(--faint)', display: 'flex', alignItems: 'center', gap: '4px' }}><Icon name="lock" size={14} /><span>{r.lock}</span></span>
                : <span />}
            </div>
          );
        })}
      </div>
    </>
  );
}
